import { QueryKey, UseQueryResult } from '@tanstack/react-query';

import {
  apiSearchSeoCityRetrieve,
  getApiSearchSeoCityRetrieveQueryKey,
  useApiSearchSeoCityRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-search-seo-city-retrieve';
import {
  apiSearchSeoCityReviewsList,
  getApiSearchSeoCityReviewsListQueryKey,
  useApiSearchSeoCityReviewsList,
} from '@rover/rsdk/src/apiClient/latest/api-search-seo-city-reviews-list';
import {
  apiSearchSeoNeighborhoodRetrieve,
  getApiSearchSeoNeighborhoodRetrieveQueryKey,
  useApiSearchSeoNeighborhoodRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-search-seo-neighborhood-retrieve';
import {
  apiSearchSeoNeighborhoodReviewsList,
  getApiSearchSeoNeighborhoodReviewsListQueryKey,
  useApiSearchSeoNeighborhoodReviewsList,
} from '@rover/rsdk/src/apiClient/latest/api-search-seo-neighborhood-reviews-list';
import type { ApiSearchSeoCityReviewsListParams } from '@rover/rsdk/src/apiClient/latest/model/apiSearchSeoCityReviewsListParams';
import type { ApiSearchSeoNeighborhoodReviewsListParams } from '@rover/rsdk/src/apiClient/latest/model/apiSearchSeoNeighborhoodReviewsListParams';
import type { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import { ErrorType } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';
import { SeoLocationDescriptor } from '@rover/utilities/seo';

type UseApiClientHookResponse<T extends (...args: any) => any> = UseQueryResult<
  Awaited<ReturnType<T>>,
  ErrorType<unknown>
> & {
  queryKey: QueryKey;
};

const neighborhoodServiceArgs = ({
  countryUrlPrefix,
  neighborhoodSlug,
  citySlug,
  regionSlug,
  serviceTypeSlug,
}: SeoLocationDescriptor) =>
  [countryUrlPrefix, neighborhoodSlug || '', citySlug, regionSlug, serviceTypeSlug] as const;

const cityServiceArgs = ({
  countryUrlPrefix,
  citySlug,
  regionSlug,
  serviceTypeSlug,
}: SeoLocationDescriptor) => [countryUrlPrefix, citySlug, regionSlug, serviceTypeSlug] as const;

// unnecessary param, but required by the urlpattern
const EXTRA = '';

type UseSearchSeoLocationResponse = UseApiClientHookResponse<
  typeof apiSearchSeoCityRetrieve | typeof apiSearchSeoNeighborhoodRetrieve
>;

export const useApiSearchSeoLocationRetrieve = (
  seoLocationDescriptor: SeoLocationDescriptor
): UseSearchSeoLocationResponse => {
  const isNeighborhood = !!seoLocationDescriptor.neighborhoodSlug;
  const cityQuery = useApiSearchSeoCityRetrieve(...cityServiceArgs(seoLocationDescriptor), EXTRA, {
    query: { enabled: !isNeighborhood },
  });
  const neighborhoodQuery = useApiSearchSeoNeighborhoodRetrieve(
    ...neighborhoodServiceArgs(seoLocationDescriptor),
    EXTRA,
    {
      query: { enabled: isNeighborhood },
    }
  );
  return isNeighborhood ? neighborhoodQuery : cityQuery;
};

export const getApiSearchSeoLocationPrefetchQueryOptions = (
  seoLocationDescriptor: SeoLocationDescriptor
): {
  queryKey: readonly string[];
  queryFn: () => Promise<SeoLocation>;
} => {
  if (seoLocationDescriptor.neighborhoodSlug) {
    const args = neighborhoodServiceArgs(seoLocationDescriptor);
    return {
      queryKey: getApiSearchSeoNeighborhoodRetrieveQueryKey(...args, EXTRA),
      queryFn: () => apiSearchSeoNeighborhoodRetrieve(...args, EXTRA),
    };
  }
  const args = cityServiceArgs(seoLocationDescriptor);
  return {
    queryKey: getApiSearchSeoCityRetrieveQueryKey(...args, EXTRA),
    queryFn: () => apiSearchSeoCityRetrieve(...args, EXTRA),
  };
};

export const useApiSearchSeoLocationReviewsList = (
  seoLocationDescriptor: SeoLocationDescriptor
): UseApiClientHookResponse<
  typeof apiSearchSeoCityReviewsList | typeof apiSearchSeoNeighborhoodReviewsList
> => {
  const isNeighborhood = !!seoLocationDescriptor.neighborhoodSlug;
  const neighborhoodQuery = useApiSearchSeoNeighborhoodReviewsList(
    ...neighborhoodServiceArgs(seoLocationDescriptor),
    {},
    { query: { enabled: isNeighborhood } }
  );
  const cityQuery = useApiSearchSeoCityReviewsList(
    ...cityServiceArgs(seoLocationDescriptor),
    {},
    { query: { enabled: !isNeighborhood } }
  );
  return isNeighborhood ? neighborhoodQuery : cityQuery;
};

export const getApiSearchSeoLocationReviewsListPrefetchQueryOptions = (
  seoLocationDescriptor: SeoLocationDescriptor
): {
  queryKey: ReadonlyArray<
    string | ApiSearchSeoNeighborhoodReviewsListParams | ApiSearchSeoCityReviewsListParams
  >;
  queryFn: () => Promise<unknown>;
} => {
  if (seoLocationDescriptor.neighborhoodSlug) {
    const args = neighborhoodServiceArgs(seoLocationDescriptor);
    return {
      queryKey: getApiSearchSeoNeighborhoodReviewsListQueryKey(...args, {}),
      queryFn: () => apiSearchSeoNeighborhoodReviewsList(...args),
    };
  }
  const args = cityServiceArgs(seoLocationDescriptor);
  return {
    queryKey: getApiSearchSeoCityReviewsListQueryKey(...args, {}),
    queryFn: () => apiSearchSeoCityReviewsList(...args),
  };
};
